/**
 * Activation retry-block state machine (extracted from GuideCard for testability).
 *
 * When AT&T rejects an activation for a non-retryable business reason, we disable the Activate button
 * until an activation *input* actually changes — so experts stop re-submitting the same doomed request.
 * The block is keyed on a fingerprint of the exact inputs sent to `activateDevice`: retrying with the
 * same fingerprint would hit the same AT&T rule, so the button stays disabled; changing any answer
 * (claim / IMEI / ICCID / token — the latter changes when the customer is re-verified) yields a new
 * fingerprint and re-enables it. A successful activation clears the block outright.
 */

import type { VerizonReplacementDevice } from "../api/prepareVerizonReplacement";

/** The exact inputs that define an activation attempt; a change to any of these is a "different" attempt. */
export interface ActivationInputs {
    activationFlowType?: string;
    claimId: string;
    imei: string;
    iccid: string;
    /** `extraAuthorizedToken || verifiedCustomerToken` — re-verifying the customer mints a new token. */
    token: string;
}

export interface BlockedActivation {
    /** The input fingerprint that was rejected. */
    signature: string;
    /** The (raw) AT&T reason surfaced to the expert. */
    reason: string;
}

/** The outcome of an `activateDevice` call, as far as the block machine cares. */
export interface ActivationResult {
    wasSuccessful: boolean;
    /** Present only when AT&T surfaced a non-retryable business reason (transient failures have none). */
    failureReason?: string;
    /**
     * Verizon only: `true` when the backend says the same inputs may still succeed, so a surfaced reason
     * is shown without blocking retry. Absent (AT&T) keeps "a surfaced reason blocks retry".
     */
    isRetryable?: boolean;
}

/**
 * Fingerprint of the inputs sent to `activateDevice`. Stable for identical inputs (so a rejection keeps
 * matching across re-renders / back-navigation) and different the moment any input changes.
 */
export function buildActivationInputSignature(inputs: ActivationInputs): string {
    return JSON.stringify({
        activationFlowType: inputs.activationFlowType,
        claimId: inputs.claimId,
        imei: inputs.imei,
        iccid: inputs.iccid,
        token: inputs.token,
    });
}

/**
 * Verizon's fingerprint: the replacement device the lookup returned, never the token. Each lookup mints a
 * new token for the same device, so a token-keyed block would lift when the expert goes back and runs the
 * lookup again, though the activation would hit the same rejection. A lookup that finds a different
 * device yields a new fingerprint.
 */
export function buildVerizonActivationInputSignature(inputs: {
    activationFlowType?: string;
    device?: VerizonReplacementDevice;
}): string {
    const { activationFlowType, device } = inputs;
    return JSON.stringify({
        activationFlowType,
        make: device?.make,
        model: device?.model,
        eSim: device?.eSim,
        lineLast4: device?.lineLast4,
        claimDate: device?.claimDate,
    });
}

/** Whether the Activate button should be blocked: a block exists AND the current inputs still match it. */
export function isRetryBlocked(blocked: BlockedActivation | undefined, currentSignature: string): boolean {
    return blocked !== undefined && blocked.signature === currentSignature;
}

/**
 * Reduce the block state after an activation attempt at `signature`:
 * - non-retryable rejection (has a reason, not marked retryable) → block these inputs;
 * - success → clear the block;
 * - transient failure (no reason), or a rejection marked retryable → leave the prior state untouched.
 */
export function nextBlockedActivation(
    prev: BlockedActivation | undefined,
    result: ActivationResult,
    signature: string,
): BlockedActivation | undefined {
    if (!result.wasSuccessful && result.failureReason !== undefined && result.isRetryable !== true) {
        return { signature, reason: result.failureReason };
    }
    if (result.wasSuccessful) {
        return undefined;
    }
    return prev;
}
